import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { APIClient } from "helpers/api_helper";
import { useNavigate } from "react-router-dom";
import useFullPageLoader from "hooks/useFullPageLoader";

const fileToBase64 = (file: File): Promise<string | ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string | ArrayBuffer);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const AddRestaurants: React.FC = () => {
  const api = new APIClient();
  const navigate = useNavigate();
  const [logo, setLogo] = useState<string | ArrayBuffer | null>(null);
  const [images, setImages] = useState<string[]>([]); // Updated type here
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imagesInputRef = useRef<HTMLInputElement | null>(null);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [categories, setCategories] = useState<any>([]);
  const [resturant_owners, setResturantOwners] = useState<any>([]);
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const base64 = await fileToBase64(file);
      setLogo(base64);
    }
  };

  const handleImages = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const base64Images = await Promise.all(
        Array.from(files).map(fileToBase64)
      );
      setImages(base64Images as string[]);
    }
  };

  const getCategories = async () => {
    let response = await api.get("v1/category/all-categories", []);
    setCategories(response.data);
  };

  const getRestaurantOwners = async () => {
    let response = await api.get("users", {
      role: "restaurant_owner",
    });
    setResturantOwners(response);
  };

  const getCountries = async () => {
    let response = await api.get(
      `${process.env.REACT_APP_LOCATION_API_URL}countries`,
      [],
      {
        Authorization: `Bearer ${process.env.REACT_APP_LOCATION_API_TOKEN}`,
      }
    );
    setCountries(response.data);
  };
  const getStates = async (countryId: string) => {
    let response = await api.get(
      `${process.env.REACT_APP_LOCATION_API_URL}states?country_id=${countryId}`,
      [],
      { Authorization: `Bearer ${process.env.REACT_APP_LOCATION_API_TOKEN}` }
    );
    setStates(response.data);
  };

  const getCities = async (stateId: string) => {
    let response = await api.get(
      `${process.env.REACT_APP_LOCATION_API_URL}cities?state_id=${stateId}`,
      [],
      { Authorization: `Bearer ${process.env.REACT_APP_LOCATION_API_TOKEN}` }
    );
    setCities(response.data);
  };

  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const countryId = e.target.value;
    if (countryId) {
      getStates(countryId);
    }
  };

  const handleStateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const stateId = e.target.value;
    if (stateId) {
      getCities(stateId);
    }
  };

  useEffect(() => {
    getRestaurantOwners();
    getCategories();
    getCountries();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    showLoader();
    e.preventDefault();
    const formData = new FormData();

    Array.from(e.currentTarget.elements).forEach((element) => {
      if (element instanceof HTMLInputElement) {
        const { name, value, type, checked, files } = element;

        if (type === "checkbox") {
          formData.append(name, checked ? "1" : "0");
        } else if (type === "file" && files?.length) {
        } else if (name) {
          formData.append(name, value);
        }
      } else if (element instanceof HTMLTextAreaElement) {
        const { name, value } = element;
        if (name) {
          formData.append(name, value);
        }
      } else if (element instanceof HTMLSelectElement) {
        formData.append(element.name, element.value);
      }
    });

    if (logo) {
      formData.append("logo", logo as string);
    }

    images.forEach((image, index) => {
      formData.append(`images[${index}]`, image);
    });

    try {
      let response = await api.create("v1/restaurant", formData);
      navigate("/restaurants");
      hideLoader();
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred while creating the restaurant.");
      hideLoader();
    }
  };

  const handleDivClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImagesClick = () => {
    if (imagesInputRef.current) {
      imagesInputRef.current.click();
    }
  };

  return (
    <>
      {loader}
      <div className="card mt-5 mx-auto">
        <div className="card-body p-1">
          <h6 className="mb-4 text-2xl font-semibold">Create Restaurant</h6>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Restaurant Form Fields */}
              {[
                {
                  id: "nameInput",
                  label: "Restaurant Name",
                  type: "text",
                  name: "name",
                  placeholder: "Enter Restaurant Name",
                },
                {
                  id: "latitudeInput",
                  label: "Latitude",
                  type: "number",
                  name: "latitude",
                  placeholder: "Enter Latitude",
                },
                {
                  id: "longitudeInput",
                  label: "Longitude",
                  type: "number",
                  name: "longitude",
                  placeholder: "Enter Longitude",
                },
                {
                  id: "zipInput",
                  label: "Zip Code",
                  type: "number",
                  name: "zip_code",
                  placeholder: "Enter Zip Code",
                },
                {
                  id: "addressInput",
                  label: "Address",
                  type: "text",
                  name: "address",
                  placeholder: "Enter Address",
                },
                {
                  id: "restaurantTypeInput",
                  label: "Restaurant Type",
                  type: "text",
                  name: "restaurantType",
                  placeholder: "Enter Restaurant Type",
                },
                {
                  id: "sittingPlanInput",
                  label: "Sitting Plan",
                  type: "text",
                  name: "sittingPlan",
                  placeholder: "Enter Sitting Plan",
                },
                {
                  id: "minPriceInput",
                  label: "Minimum Price",
                  type: "number",
                  name: "min_price",
                  placeholder: "Enter Minimum Price",
                },
                {
                  id: "maxPriceInput",
                  label: "Maximum Price",
                  type: "number",
                  name: "max_price",
                  placeholder: "Enter Maximum Price",
                },
                {
                  id: "cookingTimeInput",
                  label: "Cooking Time",
                  type: "text",
                  name: "cookingTime",
                  placeholder: "Enter Cooking Time",
                },
              ].map(({ id, label, type, name, placeholder }) => (
                <div key={id} className="mb-4">
                  <label
                    htmlFor={id}
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {label}
                  </label>
                  <input
                    type={type}
                    id={id}
                    name={name}
                    className="form-input w-full border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-zinc-500 focus:outline-none focus:border-custom-500"
                    placeholder={placeholder}
                    required
                  />
                </div>
              ))}

              <div className="mb-4">
                <label
                  htmlFor="countryIdInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Restaurant Owner
                </label>
                <select
                  id="ownerIdInput"
                  name="ownerId"
                  className="form-input w-full border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-zinc-500 focus:outline-none focus:border-custom-500"
                  required
                >
                  <option value="">Select Restaurant Owner</option>
                  {resturant_owners?.map((owner: any) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="countryIdInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Country
                </label>
                <select
                  id="countryIdInput"
                  name="countryId"
                  className="form-input w-full border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-zinc-500 focus:outline-none focus:border-custom-500"
                  required
                  onChange={handleCountryChange}
                >
                  <option value="">Select Country</option>
                  {countries.map((country: any) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* State Dropdown */}
              <div className="mb-4">
                <label
                  htmlFor="stateIdInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  State
                </label>
                <select
                  id="stateIdInput"
                  name="stateId"
                  className="form-input w-full border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-zinc-500 focus:outline-none focus:border-custom-500"
                  required
                  onChange={handleStateChange}
                  disabled={!states.length}
                >
                  <option value="">Select State</option>
                  {states.map((state: any) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* City Dropdown */}
              <div className="mb-4">
                <label
                  htmlFor="cityIdInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  City
                </label>
                <select
                  id="cityIdInput"
                  name="cityId"
                  className="form-input w-full border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-zinc-500 focus:outline-none focus:border-custom-500"
                  required
                  disabled={!cities.length}
                >
                  <option value="">Select City</option>
                  {cities.map((city: any) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Dropdown */}
              <div className="mb-4">
                <label
                  htmlFor="categoryIdInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category
                </label>
                <select
                  id="categoryIdInput"
                  name="category_id"
                  className="form-input w-full border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-zinc-500 focus:outline-none focus:border-custom-500"
                  required
                >
                  <option value="">Select Category</option>
                  {categories &&
                    categories.map((category: any) => (
                      <option key={category.id} value={category.id}>
                        {category.attributes.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Restaurant Overview */}
              <div className="mb-4">
                <label
                  htmlFor="overviewInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Restaurant Overview
                </label>
                <textarea
                  id="overviewInput"
                  name="overview"
                  className="form-input w-full border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-zinc-500 focus:outline-none focus:border-custom-500"
                  placeholder="Enter Restaurant Overview"
                  required
                />
              </div>

              {/* Logo Upload */}
              <div className="mb-4">
                <label
                  htmlFor="logoInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Upload Logo
                </label>
                <div
                  className="flex items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                  onClick={handleDivClick}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      SVG, PNG, JPG
                    </p>
                  </div>
                  <input
                    type="file"
                    id="logoInput"
                    name="logo"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={handleLogoChange}
                  />
                </div>
                {logo && (
                  <div className="mt-4 flex justify-center">
                    <img
                      src={logo as string}
                      alt="Uploaded Logo"
                      className="max-w-full h-auto"
                    />
                  </div>
                )}
              </div>

              {/* Images Upload */}
              <div className="mb-4">
                <label
                  htmlFor="ImagesInput"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Upload Images
                </label>
                <div
                  className="flex items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                  onClick={handleImagesClick}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      SVG, PNG, JPG
                    </p>
                  </div>
                  <input
                    type="file"
                    id="ImagesInput"
                    name="images"
                    className="hidden"
                    ref={imagesInputRef}
                    onChange={handleImages}
                    multiple
                  />
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-4">
                  {images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Uploaded ${index}`}
                      className="max-w-full h-auto"
                    />
                  ))}
                </div>
              </div>

              {/* Toggle Switches */}
              <div className="flex flex-col space-y-4 my-5">
                {[
                  {
                    id: "isFavouriteInput",
                    label: "Favourite",
                    name: "isFavourite",
                  },
                  {
                    id: "isFeaturedInput",
                    label: "Featured",
                    name: "isFeatured",
                  },
                  { id: "deliveryInput", label: "Delivery", name: "delivery" },
                  { id: "shippingInput", label: "Shipping", name: "shipping" },
                ].map(({ id, label, name }) => (
                  <div key={id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={id}
                      name={name}
                      className="form-checkbox h-5 w-5 text-custom-500"
                    />
                    <label
                      htmlFor={id}
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddRestaurants;
